import React, {useState, useEffect, useContext} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import "./TransferOwnershipList.css";
import API, {Resolution} from "../../../logic/API";
import {User} from "../../../logic/APITypes";
import {Button, LoadingSkeleton, TextInput} from "@spg/component_library";
import {SnackbarContext} from "../../../reusables/SnackbarProvider";
import {keycloak} from "../../utility/KeycloakProvider";

function TransferOwnershipEntry(props: {
  user: User;
  setSelectedUser: (user: User) => void;
  isDisabled: boolean;
}) {
  return (
    <button
      className="topicDescriptionCreationTransferOwnershipEntry"
      onClick={() => props.setSelectedUser(props.user)}
      disabled={props.isDisabled}
    >
      <div>{`${props.user.first_name} ${props.user.last_name}`}</div>
      <div>{props.user.email}</div>
    </button>
  );
}

type TransferOwnershipListProps = {
  /** The ID of the affected topic */
  topicId: string;
  /** Function to call when the action is aborted */
  onCancel: () => void;
  /** Function to call when the transfer succeeded */
  onTransferred: (newOwnerName: string) => void;
};

/**
 * The contents of the modal when transferring ownership of a data topic.
 * Includes a search bar, a list of users to select from and buttons to confirm or cancel.
 */
function TransferOwnershipList(props: TransferOwnershipListProps) {
  const intl = useIntl();
  const addSnackbar = useContext(SnackbarContext).addSnackbar;
  const [users, setUsers] = useState<Resolution<User[]>>({
    success: false,
    unresolved: true,
  });
  const [waitingForResponse, setWaitingForResponse] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User>();
  const [search, setSearch] = useState("");

  const simplifiedSearch = search.trim().toLowerCase();
  const searchResults = (users.payload || []).filter(
    (u) =>
      `${u.first_name} ${u.last_name}`
        .toLowerCase()
        .startsWith(simplifiedSearch) ||
      `${u.last_name} ${u.first_name}`
        .toLowerCase()
        .startsWith(simplifiedSearch) ||
      u.email.toLowerCase().includes(simplifiedSearch),
  );

  useEffect(() => {
    API.getUsersEligibleForTransfer().then((response) => {
      const users = response.payload
        ? response.payload.filter(
            (u) => u.user_id != keycloak.idTokenParsed?.sub,
          )
        : undefined;
      if (users)
        users.sort((a, b) =>
          `${a.first_name} ${a.last_name}`.localeCompare(
            `${b.first_name} ${b.last_name}`,
          ),
        );
      setUsers({
        ...response,
        payload: users,
      });
    });
  }, []);

  return (
    <div className="topicDescriptionCreationTransferOwnershipList">
      <TextInput
        label={intl.formatMessage({
          id: "topicDescriptionCreation.transferOwnership.modal.search",
        })}
        idBase={"topicDescriptionCreationTransferOwnershipListSearch"}
        placeholder={intl.formatMessage({
          id: "topicDescriptionCreation.transferOwnership.modal.search.placeholder",
        })}
        value={search}
        setValue={(v) => {
          setSearch(v);
          const simplifiedInput = v.trim().toLowerCase();
          const exactMatches = (users.payload || []).filter(
            (u) =>
              `${u.first_name} ${u.last_name}`.toLowerCase() ===
                simplifiedInput ||
              `${u.last_name} ${u.first_name}`.toLowerCase() ===
                simplifiedInput ||
              u.email.toLowerCase() === simplifiedInput,
          );
          if (exactMatches.length === 1) setSelectedUser(exactMatches[0]);
          else setSelectedUser(undefined);
        }}
        isDisabled={waitingForResponse}
      />
      {!users.unresolved && (!users.success || searchResults.length === 0) && (
        <div className="topicDescriptionCreationTransferOwnershipListBlank">
          <FormattedMessage
            id={
              "topicDescriptionCreation.transferOwnership.modal." +
              (!users.success ? "error" : "empty")
            }
          />
        </div>
      )}
      <ul className="defaultScrollbar">
        {users.unresolved
          ? [1, 2, 3].map((i) => (
              <li key={i}>
                <div className="topicDescriptionCreationTransferOwnershipEntry">
                  <LoadingSkeleton />
                </div>
              </li>
            ))
          : searchResults.map((u) => (
              <li key={u.user_id}>
                <TransferOwnershipEntry
                  user={u}
                  setSelectedUser={(user) => {
                    setSearch(user.email);
                    setSelectedUser(user);
                  }}
                  isDisabled={waitingForResponse}
                />
              </li>
            ))}
      </ul>
      <div className="topicDescriptionCreationTransferOwnershipModalButtons">
        <Button
          label={intl.formatMessage({
            id: "topicDescriptionCreation.transferOwnership.modal.cancel",
          })}
          idBase={"topicDescriptionCreationTransferOwnershipListModalCancel"}
          onClick={props.onCancel}
          configuration={"tertiary"}
          isDisabled={waitingForResponse}
        />
        <Button
          label={intl.formatMessage({
            id: "topicDescriptionCreation.transferOwnership.modal.confirm",
          })}
          isDisabled={waitingForResponse || !selectedUser}
          idBase={"topicDescriptionCreationTransferOwnershipListModalConfirm"}
          onClick={() => {
            if (!selectedUser) return;
            setWaitingForResponse(true);
            API.transferOwnershipOfTopic(
              props.topicId,
              selectedUser.user_id,
            ).then((response) => {
              setWaitingForResponse(false);
              if (response.success)
                props.onTransferred(
                  `${selectedUser.first_name} ${selectedUser.last_name}`,
                );
              else {
                props.onCancel();
                addSnackbar({
                  variant: "close",
                  text: intl.formatMessage({
                    id: "topicDescriptionCreation.transferOwnership.snackbar.error",
                  }),
                  hasCloseButton: true,
                });
              }
            });
          }}
          configuration={"primary"}
        />
      </div>
    </div>
  );
}

export {TransferOwnershipList};
